import { ClipboardCheck, Gauge, Pencil, Send, SendHorizontal } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { surveyTrainingRecommendationsApi } from '../surveyApi';
import {
  TRAINING_RECOMMENDATION_STATUS,
  TRAINING_RECOMMENDATION_STATUS_LABELS,
  isRecommendationLocked,
  type SurveyTrainingRecommendation,
} from '../surveyTypes';
import { SurveyTrainingRecommendationModal } from './SurveyTrainingRecommendationModal';
import { SurveyTrainingRecommendationsAddModal } from './SurveyTrainingRecommendationsAddModal';
import { SurveyTrainingImpactModal } from './SurveyTrainingImpactModal';
import { SurveyTrainingRecommendationReviewModal } from './SurveyTrainingRecommendationReviewModal';

/** Why a recommendation cannot be changed right now — a STATE, said in the server's words. */
function lockedReason(row: SurveyTrainingRecommendation): string | null {
  return row.status === TRAINING_RECOMMENDATION_STATUS.Submitted
    ? 'This training recommendation is waiting for approval. Retract it from Approvals first.'
    : row.status === TRAINING_RECOMMENDATION_STATUS.Approved
      ? 'This training recommendation has been approved, so it can no longer be changed.'
      : null;
}

/**
 * A development plan's Training Recommendations — the standard panel table, with Create (product
 * call, 12 Oct 2026). A plan holds several; each is its own record with its own approval.
 *
 * - **Title** is the row's anchor and opens the recommendation's PROFILE, where its approval is
 *   tracked.
 * - The row menu carries Edit and Delete (`surveys.plan`), Submit for approval
 *   (`surveys.submit-plan`), Review (`approvals.decide`), Send to employees (`surveys.send-plan`)
 *   and Impact assessment (`surveys.assess-impact`) — each disabled for STATE with the reason. Review is where an approver approves or declines; nothing else marks one approved.
 */
export function SurveyTrainingRecommendationsPanel({
  surveyId,
  planId,
  planActive,
}: {
  surveyId: string;
  planId: string;
  planActive: boolean;
}) {
  const navigate = useNavigate();
  const toast = useToast();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SurveyTrainingRecommendation | null>(null);
  const [reviewing, setReviewing] = useState<SurveyTrainingRecommendation | null>(null);
  const [confirming, setConfirming] = useState<{ kind: 'submit' | 'send' | 'delete'; row: SurveyTrainingRecommendation } | null>(null);
  const [assessingImpact, setAssessingImpact] = useState<SurveyTrainingRecommendation | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchPage = useCallback(
    (query: DataTableQuery, signal: AbortSignal) => surveyTrainingRecommendationsApi.list(planId, query, signal),
    [planId],
  );

  const state = useDataTable<SurveyTrainingRecommendation>({ fetchPage, pageSize: 10, defaultSort: 'createdAt:desc' });

  const deletedPlan = planActive ? null : 'This development plan is deleted, so its training recommendations cannot be changed.';

  const rowActions = useCallback(
    (row: SurveyTrainingRecommendation): MenuItem[] => [
      {
        // In the menu rather than the table's own Edit, because it is disabled for STATE — an
        // approver holds it, or has approved it — and that needs its reason said.
        key: `edit-${row.id}`,
        label: 'Edit',
        icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
        permission: 'surveys.plan',
        disabledReason: deletedPlan ?? lockedReason(row),
        onClick: () => setEditing(row),
      },
      {
        /*
          REVIEW — reads the recommendation, then Approve (remarks optional) or Decline (remarks
          required). The engine's key; DISABLED for state where nothing is waiting on a decision.
          Whether it waits on THIS reader is the engine's answer, which the window reads and says.
        */
        key: `review-${row.id}`,
        label: 'Review',
        icon: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
        permission: 'approvals.decide',
        disabledReason:
          row.status === TRAINING_RECOMMENDATION_STATUS.Submitted
            ? null
            : row.status === TRAINING_RECOMMENDATION_STATUS.Approved
              ? 'This training recommendation has already been approved.'
              : 'This training recommendation has not been submitted for approval.',
        onClick: () => setReviewing(row),
      },
      {
        key: `submit-${row.id}`,
        label: 'Submit for approval',
        icon: <Send className="h-4 w-4" aria-hidden="true" />,
        permission: 'surveys.submit-plan',
        disabledReason:
          deletedPlan ??
          (row.status === TRAINING_RECOMMENDATION_STATUS.Submitted
            ? 'This training recommendation is already waiting for approval.'
            : row.status === TRAINING_RECOMMENDATION_STATUS.Approved
              ? 'This training recommendation has already been approved.'
              : null),
        onClick: () => setConfirming({ kind: 'submit', row }),
      },
      {
        // SEND TO EMPLOYEES — the approved recommendation reaches the employees on its plan. Once.
        key: `send-${row.id}`,
        label: 'Send to employees',
        icon: <SendHorizontal className="h-4 w-4" aria-hidden="true" />,
        permission: 'surveys.send-plan',
        disabledReason:
          deletedPlan ??
          (row.sentAt != null
            ? 'This training recommendation has already been sent to the employees.'
            : row.status !== TRAINING_RECOMMENDATION_STATUS.Approved
              ? 'Only an approved training recommendation can be sent to the employees.'
              : null),
        onClick: () => setConfirming({ kind: 'send', row }),
      },
      {
        // IMPACT ASSESSMENT — what the training changed for each employee it was sent to.
        key: `impact-${row.id}`,
        label: 'Impact assessment',
        icon: <Gauge className="h-4 w-4" aria-hidden="true" />,
        permission: 'surveys.assess-impact',
        disabledReason:
          deletedPlan ?? (row.sentAt == null ? 'This training recommendation has not been sent to the employees yet.' : null),
        onClick: () => setAssessingImpact(row),
      },
    ],
    [deletedPlan],
  );

  const columns = useMemo<DataColumn<SurveyTrainingRecommendation>[]>(
    () => [
      { key: 'title', header: 'Title', width: 360, sortable: true, render: (row) => <TextCell value={row.title} /> },
      {
        // Where it stands with its approver — its lifecycle, not whether the row is live.
        // Keyed apart from `status`: that key is the row's LIVE state, which this is not.
        key: 'approvalStatus',
        header: 'Status',
        width: 160,
        stage: true,
        summary: true,
        render: (row) => <StatusText tone="inactive">{TRAINING_RECOMMENDATION_STATUS_LABELS[row.status]}</StatusText>,
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'submit') await surveyTrainingRecommendationsApi.submit(confirming.row.id);
      else if (confirming.kind === 'send') await surveyTrainingRecommendationsApi.send(confirming.row.id);
      else await surveyTrainingRecommendationsApi.deactivate(confirming.row.id);

      toast.success(
        confirming.kind === 'submit'
          ? `Submitted "${confirming.row.title}" for approval.`
          : confirming.kind === 'send'
            ? `Sent "${confirming.row.title}" to the employees.`
            : `Deleted "${confirming.row.title}".`,
      );
      setConfirming(null);
      state.reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    // The tab is `bleed`, so the inset is this wrapper's — as on the survey's own table tabs.
    <div className="flex h-full min-w-0 flex-col p-4">
      <DataTablePanel<SurveyTrainingRecommendation>
        id="training-recommendations"
        state={state}
        columns={columns}
        rowKey={(row) => row.id}
        searchPlaceholder="Search training recommendations"
        fill
        onCreate={() => setCreating(true)}
        createPermission="surveys.plan"
        createDisabledReason={deletedPlan}
        // The TITLE opens the recommendation's profile — a page, where its approval is tracked.
        // Under the survey's own path: `/learning-development/training-recommendation` is the
        // register's, on that module's key, and would light its rail entry instead of Survey's.
        onRowOpen={(row) =>
          navigate(`/learning-development/survey/${surveyId}/plan/${planId}/training-recommendation/${row.id}`)
        }
        openPermission="surveys.read"
        rowActions={rowActions}
        onDelete={(row) => setConfirming({ kind: 'delete', row })}
        deletePermission="surveys.plan"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) =>
          deletedPlan ??
          (row.status === TRAINING_RECOMMENDATION_STATUS.Approved
            ? 'This training recommendation has been approved, so it can no longer be deleted.'
            : null)
        }
        otherInfo={(row) => row}
        emptyMessage="No training has been recommended on this plan yet."
        emptySearchMessage="No training recommendation's title matches that search."
      />

      {/* CREATE is the inline grid, several at once; EDIT is one recommendation's own form. */}
      {creating ? (
        <SurveyTrainingRecommendationsAddModal
          open
          planId={planId}
          onClose={() => {
            setCreating(false);
            // A row saved before a later one was refused is already on the plan.
            state.reload();
          }}
          onSaved={() => {
            setCreating(false);
            state.reload();
          }}
        />
      ) : null}

      {editing !== null ? (
        <SurveyTrainingRecommendationModal
          open
          planId={planId}
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            state.reload();
          }}
        />
      ) : null}

      {reviewing ? (
        <SurveyTrainingRecommendationReviewModal
          open
          recommendationId={reviewing.id}
          title={reviewing.title}
          onClose={() => setReviewing(null)}
          onDecided={() => {
            setReviewing(null);
            state.reload();
          }}
        />
      ) : null}

      {assessingImpact ? (
        <SurveyTrainingImpactModal
          open
          recommendation={assessingImpact}
          onClose={() => setAssessingImpact(null)}
          onSaved={() => setAssessingImpact(null)}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={
            confirming.kind === 'submit'
              ? 'Submit training recommendation'
              : confirming.kind === 'send'
                ? 'Send training recommendation'
                : 'Delete training recommendation'
          }
          danger={confirming.kind === 'delete'}
          requireConfirm
          // The destructive half asks for the password too; submitting is an ordinary act of the job.
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`training-recommendation-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'submit'
                  ? `"${confirming.row.title}" goes to its approver under Approvals. It cannot be changed while they hold it, or once it is approved.`
                  : confirming.kind === 'send'
                    ? `"${confirming.row.title}" is sent to every employee on this plan, who can read it and answer it from then on. It cannot be unsent.`
                    : `"${confirming.row.title}" is deleted${
                      isRecommendationLocked(confirming.row.status) ? ', and its pending approval is closed' : ''
                    }. Nothing is erased.`}
              </p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
